// Shared date/time formatting utilities for EVENT★GOERS

/**
 * Format date to "Month Day, Year" format (e.g., "February 15, 2025")
 * @param {string} dateString - Date string in any parseable format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

/**
 * Format date to short format "Mon Day" (e.g., "Feb 15")
 * @param {string} dateString - Date string in any parseable format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

/**
 * Format time from 24-hour to 12-hour format (e.g., "20:00" -> "8:00 PM")
 * @param {string} timeString - Time string in 24-hour format (HH:MM)
 * @returns {string} Formatted time string
 */
export const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    // Handle various time formats
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours)) return timeString;
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes?.toString().padStart(2, '0') || '00'} ${period}`;
  } catch {
    return timeString;
  }
};

/**
 * Format date and time together
 * @param {string} dateString - Date string
 * @param {string} timeString - Time string
 * @returns {string} Combined formatted string
 */
export const formatDateTime = (dateString, timeString) => {
  const date = formatDate(dateString);
  const time = formatTime(timeString);
  if (date && time) return `${date} at ${time}`;
  return date || time || '';
};

/**
 * Get relative time description (e.g., "Tomorrow", "This Saturday")
 * @param {string} dateString - Date string
 * @returns {string} Relative description or formatted date
 */
export const getRelativeDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    }
    
    return formatDate(dateString);
  } catch {
    return dateString;
  }
};
